import type { SceneryObject, SceneryScene, SceneryRect } from '@/modules/game-protocol/world-scenery';
export type ScenePoint = {
    x: number;
    y: number;
};
export interface SceneSnapshot {
    version: number;
    center: ScenePoint;
    heroPosition: ScenePoint;
    bounds: SceneryRect;
    biome: string;
    objects: readonly SceneryObject[];
    scenes: readonly (SceneryScene & {
        groundPatches?: readonly {
            centerX: number;
            centerY: number;
            radius: number;
            style: string;
        }[];
    })[];
    roads: readonly (readonly ScenePoint[])[];
    trails: readonly (readonly ScenePoint[])[];
}
export interface SceneInvestigation {
    version: number;
    id: string;
    sceneId: string;
    sceneKey: string;
    worldRevision: string;
    startedAtSim: string;
    completedAtSim: string;
    observedAtUTC: string;
    status: string;
    thought: {
        key: string;
        variant: number;
        gender: string;
        reaction: string;
        text: {
            ru: string;
            en: string;
        };
    };
    effect: {
        kind: string;
        applied: boolean;
        text: {
            ru: string;
            en: string;
        };
    };
    snapshot?: SceneSnapshot;
}
export function readSceneInvestigation(value: unknown): SceneInvestigation | undefined {
    if (!value || typeof value !== 'object')
        return;
    const r = value as SceneInvestigation;
    if (r.version !== 1 || typeof r.sceneId !== 'string' || typeof r.worldRevision !== 'string' || typeof r.thought?.text?.ru !== 'string' || typeof r.thought?.text?.en !== 'string' || typeof r.effect?.text?.ru !== 'string' || typeof r.effect?.text?.en !== 'string')
        return;
    return r;
}
export function isSceneSnapshot(value: unknown): value is SceneSnapshot {
    if (!value || typeof value !== 'object')
        return false;
    const s = value as SceneSnapshot, b = s.bounds;
    const point = (p: ScenePoint) => p && Number.isFinite(p.x) && Number.isFinite(p.y);
    const rect = (r: SceneryRect) => r && [r.minX, r.minY, r.maxX, r.maxY].every(Number.isFinite);
    const object = (o: SceneryObject) => o && typeof o.id === 'string' && typeof o.textureKey === 'string' && Number.isFinite(o.worldX) && Number.isFinite(o.worldY);
    const paths = (list: SceneSnapshot['roads']) => Array.isArray(list) && list.length <= 256 && list.every(p => Array.isArray(p) && p.length <= 1024 && p.every(point));
    return s.version === 1 && point(s.center) && point(s.heroPosition) && rect(b) && b.maxX > b.minX && b.maxY > b.minY && b.maxX - b.minX <= 32 && b.maxY - b.minY <= 32 && typeof s.biome === 'string'
        && Array.isArray(s.objects) && s.objects.length <= 256 && s.objects.every(object)
        && Array.isArray(s.scenes) && s.scenes.length <= 256 && s.scenes.every(scene => scene && typeof scene.id === 'string' && Array.isArray(scene.clearings) && scene.clearings.every(rect) && Array.isArray(scene.objects) && scene.objects.every(object) && (!scene.groundPatches || (Array.isArray(scene.groundPatches) && scene.groundPatches.every((p: {
        centerX: number;
        centerY: number;
        radius: number;
        style: string;
    }) => [p.centerX, p.centerY, p.radius].every(Number.isFinite) && p.radius > 0 && typeof p.style === 'string'))))
        && paths(s.roads) && paths(s.trails);
}
